import BlogItemEntity from '../BlogItemEntity.js';
import {Element} from '@chaysehartsuff/active-html';
import Quill from 'quill';
import Delta from 'quill-delta';
import ImageResize from 'quill-resize-module';
import PhotoManager from '../../photo/PhotoManager.js';
import {Image} from '@chaysehartsuff/active-html';
import QuillTableBetter from 'quill-table-better';
import 'quill-table-better/dist/quill-table-better.css';
import 'quill-resize-module/dist/resize.css';

const Font = Quill.import('formats/font');
Font.whitelist = [
    'raleway',
    'sans',
    'serif',
    'mono',
    'arial',
    'georgia',
    'times-new-roman',
    'courier-new',
];

Quill.register(Font, true);
Quill.register('modules/resize', ImageResize);
Quill.register({'modules/table-better': QuillTableBetter}, true);

export default class QuillTextbox extends BlogItemEntity {

    /**
     * @type {Quill} quillEditor
     */
    quillEditor;

    /**
     * @type {Delta} quillDelta
     */
    quillDelta;

    constructor(){
        super(); 
        this.previewContainer.addClass('quill-preview ql-editor');
    }

    /**
     * Custom handler for the Quill image button.
     */
    imageHandler() {
        let photoManager = new PhotoManager(true, false);
        this.addContent(photoManager);

        photoManager.addListener(PhotoManager.onUserSelectionEvent, (uuid) => {
            const range = this.quillEditor.getSelection(true);
            const imageHtml = new Image("/image/" + uuid).compile();
            
            // Insert the compiled image HTML into the editor at the current cursor position.
            this.quillEditor.clipboard.dangerouslyPasteHTML(range.index, imageHtml);

            // Clean up and restore the view
            this.removeContent(photoManager);
        });

        photoManager.addListener(PhotoManager.onCancelEvent, (manager) => {
            // On cancel, just remove the photo manager and show the editor again.
            this.removeContent(photoManager);
        });
    }

    normalizeDelta(deltaData) {
        if (!deltaData) {
            return null;
        }

        if (typeof deltaData === 'string') {
            try {
                return this.normalizeDelta(JSON.parse(deltaData));
            } catch (_error) {
                return null;
            }
        }

        if (deltaData instanceof Delta) {
            return deltaData;
        }

        if (Array.isArray(deltaData)) {
            return new Delta(deltaData);
        }

        if (Array.isArray(deltaData.ops)) {
            return new Delta(deltaData.ops);
        }

        return null;
    }

    onEdit(e) {
        let quillEditor = new Element().setId('quill-editor-' + this.getId());
        this.editorContainer.addContent(quillEditor);
        this.quillEditor = new Quill('#quill-editor-' + this.getId(), {
            modules: {
                toolbar: {
                    container: [
                        [{ 'font': Font.whitelist }],
                        [{ 'size': ['small', false, 'large', 'huge'] }],
                        [{ 'header': [1, 2, 3, 4, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'color': [] }, { 'background': [] }],
                        ['blockquote', 'code-block'],
                        [{ 'script': 'sub' }, { 'script': 'super' }],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'indent': '-1' }, { 'indent': '+1' }],
                        [{ 'direction': 'rtl' }],
                        [{ 'align': [] }],
                        ['link', 'image', 'video'],
                        [{ 'table-better': '' }],
                        ['clean']
                    ],
                    handlers: {
                        'image': this.imageHandler.bind(this) // Bind our custom handler
                    }
                },
                resize: {
                    displaySize: true,
                },
                table: false,
                'table-better': {
                    toolbarTable: true,
                },
                keyboard: {
                    bindings: QuillTableBetter.keyboardBindings,
                }
            },
            theme: 'snow'
        });
        const normalizedDelta = this.normalizeDelta(this.quillDelta);
        if(normalizedDelta) {
            // quill-table-better docs: use updateContents for initializing table deltas.
            this.quillEditor.setText('');
            this.quillEditor.updateContents(normalizedDelta, Quill.sources.USER);
            this.quillEditor.setSelection(0, Quill.sources.SILENT);
        } else {
            if (typeof this.text === 'string' && this.text.trim().length > 0) {
                this.quillEditor.clipboard.dangerouslyPasteHTML(this.text);
            } else {
                this.quillEditor.setText('');
            }
        }
        this.quillEditor.format('font', 'raleway');
        this.stripInlineWidths();
    }
    onSave(e) {
        this.text = this.quillEditor.getSemanticHTML().replace(/&nbsp;/g, ' ');
        this.quillDelta = this.quillEditor.getContents();
        this.event(BlogItemEntity.onChangeEvent);
    }
    exportObject(){
        return {
            ...super.exportObject(),
            quillDelta: this.quillDelta,
        }
    }
    importObject(obj){
        super.importObject(obj);
        this.previewContainer.setContent(this.text);
    }

    stripInlineWidths() {
        // Target the table and any temporary tags inside it
        const tables = document.querySelectorAll('table.ql-table-better');
        const tempTags = document.querySelectorAll('temporary.ql-table-temporary');

        // Only remove inline width from the table if it's exactly '100%'
        tables.forEach(table => {
            if (table.style.width === '100%') {
                table.style.removeProperty('width');
            }
        });

        // Only remove inline width from the temporary tag if it's exactly '100%'
        tempTags.forEach(temp => {
            if (temp.style.width === '100%') {
                temp.style.removeProperty('width');
            }
        });
    }
}